import { snapshot } from 'valtio';
import { getChosenOrganization } from '../../organizations/store-actions/organization-store-actions';
import connectionsService from '../services';
import SelectedConnectionStore from '../stores/slected-connection-store';
import {
  ConfigureConnectionInput,
  Connection,
  ConnectionError,
  ConnectionFormFields,
  ConnectionInput,
} from '../types/connection-types';
import {
  clearErrorMessage,
  setErrorMessage,
  setIsLoadingSave,
} from '../utils/connection-configure-store-utils';
import { updateConnections } from '../utils/connections-store-utils';
import {
  closeConnectionModal,
  setIsLoadingAdd,
  setIsLoadingRemove,
  updateConnectionState,
} from '../utils/selected-connection-store-utils';

export const addConnection = async (
  configureConnectionInput: ConfigureConnectionInput,
): Promise<void> => {
  try {
    clearErrorMessage();
    setIsLoadingAdd(true);

    const addConnectionResponse =
      await connectionsService.addConnection.execute(configureConnectionInput);

    if (addConnectionResponse === true) {
      updateConnectionState(configureConnectionInput.connectionId, true);
      closeConnectionModal();
    } else {
      setErrorMessage(ConnectionError.FailedToAddConnection);
    }
  } finally {
    setIsLoadingAdd(false);
  }
};

export const removeConnection = async (
  configureConnectionInput: ConfigureConnectionInput,
): Promise<void> => {
  try {
    clearErrorMessage();
    setIsLoadingRemove(true);

    const deleteConnectionResponse =
      await connectionsService.deleteConnection.execute(
        configureConnectionInput,
      );

    if (deleteConnectionResponse === true) {
      updateConnectionState(configureConnectionInput.connectionId, false);
      closeConnectionModal();
    } else {
      setErrorMessage(ConnectionError.FailedToRemoveConnection);
    }
  } finally {
    setIsLoadingRemove(false);
  }
};

export const configureConnection = async (
  connectionId: string,
  connectionFormFields: ConnectionFormFields,
): Promise<void> => {
  try {
    clearErrorMessage();
    setIsLoadingSave(true);

    const organization = getChosenOrganization();
    if (!organization) {
      setErrorMessage(ConnectionError.FailedToSave);
      return;
    }

    const connectionInput: ConnectionInput = {
      name: `${connectionFormFields.strategy}-${organization.name}`,
      ...connectionFormFields,
    };

    //// in case there is no connection id we will create a new connection as there isn't any connection of this type for the org
    if (!connectionId) {
      const connection = await connectionsService.createConnection.execute(
        connectionInput,
      );

      if (!connection) {
        setErrorMessage(ConnectionError.FailedToSave);
        return;
      }

      updateConnections(connection);
      closeConnectionModal();
      return;
    }

    // in case of an update
    const connection = await connectionsService.updateConnection.execute(
      connectionId,
      connectionInput,
    );

    if (!connection) {
      setErrorMessage(ConnectionError.FailedToSave);
      return;
    }

    updateConnections(connection);
    if (!connection.enabled) {
      const configureConnectionInput: ConfigureConnectionInput = {
        connectionId: connection.id,
      };

      await addConnection(configureConnectionInput);
    }
    closeConnectionModal();
  } finally {
    setIsLoadingSave(false);
  }
};

export const checkIsSelfConnection = (connection: Connection) => {
  const { loggedInUserStrategy } = snapshot(SelectedConnectionStore);

  return loggedInUserStrategy === connection.strategy;
};
